import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadCSS, loadScript } from '../../../utils/scriptLoader';
import {
  TAX_PROPERTIES,
  REGION_CONFIG,
  type RegionFilter,
  type TaxProperty,
} from '../../../mocks/taxProperties';

/* ── CDN URLs ────────────────────────────────────────────────── */
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const CLUSTER_JS  = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';

/* ── Region counts helper ────────────────────────────────────── */
const countByRegion = (filter: RegionFilter) =>
  filter === 'ALL'
    ? TAX_PROPERTIES.length
    : TAX_PROPERTIES.filter((p) => p.region === filter).length;

/* ── Property Detail Sidebar ─────────────────────────────────── */
interface PropertySidebarProps {
  property: TaxProperty | null;
  onClose: () => void;
}

function PropertySidebar({ property, onClose }: PropertySidebarProps) {
  if (!property) return null;

  const cfg = REGION_CONFIG[property.region];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[2500] transition-opacity duration-300"
        onClick={onClose}
      />
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0d1520] border-l border-white/10 z-[2501] transform transition-transform duration-300 ease-out translate-x-0 shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0d1520] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: cfg.color }}
            />
            <span className="text-white font-semibold">Property Details</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Region & State Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: `${cfg.color}22`, color: cfg.color }}
            >
              {property.state}
            </span>
            <span className="text-white/50 text-xs">{property.county} County</span>
          </div>

          {/* Address */}
          <h3 className="text-white text-xl font-bold leading-tight mb-6">
            {property.address}
          </h3>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-white/5 rounded-xl p-4 border border-white/8">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Assessed Value</div>
              <div className="text-white text-lg font-bold">{property.assessed}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/8">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Tax Owed</div>
              <div className="text-red-400 text-lg font-bold">{property.taxOwed}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/8">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Minimum Bid</div>
              <div className="text-amber-400 text-lg font-bold">{property.minBid}</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/8">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">Auction Date</div>
              <div className="text-white text-sm font-semibold">{property.auctionDate}</div>
            </div>
          </div>

          {/* Property Details */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/8 mb-6">
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <i className="ri-home-4-line text-white/40" />
              Property Information
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">APN</span>
                <span className="text-white text-sm font-mono">{property.apn || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">Property Type</span>
                <span className="text-white text-sm">{property.propertyType || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">Square Footage</span>
                <span className="text-white text-sm">{property.sqft?.toLocaleString() || 'N/A'} sqft</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-white/5">
                <span className="text-white/40 text-sm">Year Built</span>
                <span className="text-white text-sm">{property.yearBuilt || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-white/40 text-sm">Status</span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-sm font-semibold">{property.status || 'Tax Delinquent'}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/8 mb-6">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <i className="ri-map-pin-line text-white/40" />
              Location
            </h4>
            <div className="text-white/60 text-sm leading-relaxed">
              {property.address}
            </div>
            <div className="mt-2 text-white/40 text-xs">
              Lat: {property.lat.toFixed(4)}, Lng: {property.lng.toFixed(4)}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 w-full py-4 bg-amber-500 hover:bg-amber-400 text-[#070c14] font-bold rounded-xl transition-colors whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line" />
              Request Acquisition Service
            </Link>
            <Link
              to="/property-listings"
              className="flex items-center justify-center gap-2 w-full py-3.5 border border-white/15 text-white/70 font-semibold rounded-xl hover:border-white/30 hover:text-white transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-list-check" />
              View All Properties
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Map sub-component ───────────────────────────────────────── */
interface MapProps {
  filter: RegionFilter;
  stateFilter: string | null;
  onPropertySelect: (property: TaxProperty) => void;
}

function PropertyMap({ filter, stateFilter, onPropertySelect }: MapProps) {
  const containerRef   = useRef<HTMLDivElement>(null);
  const mapRef         = useRef<unknown>(null);
  const clusterRef     = useRef<unknown>(null);
  const [ready, setReady] = useState(false);

  /* Bootstrap Leaflet once */
  useEffect(() => {
    let cancelled = false;
    loadCSS(LEAFLET_CSS);

    const boot = async () => {
      await loadScript(LEAFLET_JS);
      await loadScript(CLUSTER_JS);
      if (cancelled || !containerRef.current) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const L = (window as any).L;
      if (!L || mapRef.current) return;

      const map = L.map(containerRef.current, {
        center: [39.5, -98.35],
        zoom: 4,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: true,
      });
      mapRef.current = map;

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        { subdomains: 'abcd', maxZoom: 19 }
      ).addTo(map);

      L.control.attribution({ prefix: false, position: 'bottomright' })
        .addAttribution('&copy; <a href="https://carto.com/" style="color:#f59e0b">CARTO</a>')
        .addTo(map);

      L.control.zoom({ position: 'topright' }).addTo(map);

      /* Popup CSS */
      if (!document.getElementById('tax-map-popup-style')) {
        const style = document.createElement('style');
        style.id = 'tax-map-popup-style';
        style.innerHTML = `
          .tax-map-popup .leaflet-popup-content-wrapper {
            background: transparent !important;
            box-shadow: 0 8px 40px rgba(0,0,0,0.7) !important;
            padding: 0 !important;
            border-radius: 12px !important;
          }
          .tax-map-popup .leaflet-popup-content { margin: 0 !important; }
          .tax-map-popup .leaflet-popup-tip { background: #1c2132 !important; }
          .leaflet-zoom-in, .leaflet-zoom-out {
            background: #141825 !important; color: #f59e0b !important;
            border-color: rgba(255,255,255,0.08) !important;
          }
          .leaflet-zoom-in:hover, .leaflet-zoom-out:hover { background: #1c2132 !important; }
          .leaflet-bar { border: 1px solid rgba(255,255,255,0.08) !important; box-shadow: none !important; }
          .leaflet-control-attribution {
            background: rgba(7,12,20,0.75) !important;
            color: rgba(255,255,255,0.3) !important;
            font-size: 9px !important; padding: 2px 6px !important;
          }
          .leaflet-control-attribution a { color: #f59e0b !important; }
        `;
        document.head.appendChild(style);
      }

      setReady(true);
    };

    boot();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (mapRef.current as any).remove();
        mapRef.current = null;
        clusterRef.current = null;
      }
    };
  }, []);

  /* Rebuild markers when filter or ready changes */
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L   = (window as any).L;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = mapRef.current as any;

    if (clusterRef.current) {
      map.removeLayer(clusterRef.current);
      clusterRef.current = null;
    }

    const filtered: TaxProperty[] = (() => {
      let base: TaxProperty[] = filter === 'ALL'
        ? TAX_PROPERTIES
        : TAX_PROPERTIES.filter((p) => p.region === filter);
      if (stateFilter) base = base.filter((p) => p.state === stateFilter);
      return base;
    })();

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (c: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const count = (c as any).getChildCount();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (c as any).getAllChildMarkers() as any[];
        const regions = children.map((m) => m.options.region as string);
        const freq: Record<string, number> = {};
        regions.forEach((r) => { freq[r] = (freq[r] || 0) + 1; });
        const dominant = (Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'NORTHEAST') as RegionFilter;
        const cfg = REGION_CONFIG[dominant as keyof typeof REGION_CONFIG] ?? REGION_CONFIG.NORTHEAST;
        const size = count > 20 ? 52 : count > 8 ? 44 : 36;
        return L.divIcon({
          className: '',
          iconSize:  [size, size],
          iconAnchor: [size / 2, size / 2],
          html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            background:${cfg.clusterBg};
            border:2.5px solid ${cfg.color}99;
            display:flex;align-items:center;justify-content:center;
            font-weight:700;font-size:${count > 20 ? 13 : 11}px;
            color:#0a0700;
            box-shadow:0 0 18px ${cfg.color}55;
          ">${count}</div>`,
        });
      },
    });

    filtered.forEach((prop) => {
      const cfg = REGION_CONFIG[prop.region];
      const marker = L.circleMarker([prop.lat, prop.lng], {
        radius:      8,
        fillColor:   cfg.color,
        color:       '#ffffff',
        weight:      2,
        fillOpacity: 0.9,
        region:      prop.region,
      });

      // Click handler to open sidebar instead of popup
      marker.on('click', () => {
        onPropertySelect(prop);
      });

      // Keep popup for hover preview
      marker.bindPopup(`
        <div style="
          background:#141825;border:1px solid rgba(255,255,255,0.1);
          border-radius:12px;padding:16px 18px;min-width:230px;
          font-family:'Inter',system-ui,sans-serif;
        ">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
            <span style="
              background:${cfg.color}22;color:${cfg.color};
              font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;
            ">${prop.state}</span>
            <span style="color:#fff;font-size:12px;font-weight:600;">${prop.county} County</span>
          </div>
          <p style="color:rgba(255,255,255,0.65);font-size:12px;line-height:1.5;margin:0 0 12px;">
            ${prop.address}
          </p>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;">
            <div style="background:rgba(255,255,255,0.04);border-radius:8px;padding:8px 10px;">
              <div style="color:rgba(255,255,255,0.35);font-size:9px;text-transform:uppercase;letter-spacing:.06em;margin-bottom:3px;">Assessed Value</div>
              <div style="color:#fff;font-size:14px;font-weight:700;">${prop.assessed}</div>
            </div>
            <div style="background:rgba(255,255,255,0.04);border-radius:8px;padding:8px 10px;">
              <div style="color:rgba(255,255,255,0.35);font-size:9px;text-transform:uppercase;letter-spacing:.06em;margin-bottom:3px;">Tax Status</div>
              <div style="color:#ef4444;font-size:12px;font-weight:700;display:flex;align-items:center;gap:4px;">
                <span style="width:5px;height:5px;border-radius:50%;background:#ef4444;display:inline-block;"></span>
                Tax Delinquent
              </div>
            </div>
          </div>
          <div style="color:rgba(255,255,255,0.4);font-size:10px;text-align:center;">
            Click pin for full details
          </div>
        </div>
      `, { maxWidth: 270, className: 'tax-map-popup' });

      cluster.addLayer(marker);
    });

    map.addLayer(cluster);
    clusterRef.current = cluster;
  }, [ready, filter, stateFilter, onPropertySelect]);

  return <div ref={containerRef} className="absolute inset-0" />;
}

/* ── Paywall overlay ─────────────────────────────────────────── */
function PaywallOverlay() {
  return (
    <div
      className="absolute inset-0 z-[2000] flex items-center justify-center"
      style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', background: 'rgba(7,12,20,0.72)' }}
    >
      <div className="bg-[#0d1520] border border-white/12 rounded-2xl p-8 md:p-10 max-w-md w-full mx-6 text-center">
        {/* Lock icon */}
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/25 mx-auto mb-5">
          <i className="ri-lock-2-line text-amber-400 text-2xl" />
        </div>

        <h3 className="font-playfair text-2xl font-bold text-white mb-3">
          Member Access Required
        </h3>
        <p className="text-white/50 text-sm leading-relaxed mb-2">
          Join to unlock the full Live Tax Map with
        </p>
        <p className="text-amber-400 font-bold text-lg mb-6">
          10,000+ active delinquent properties nationwide
        </p>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-8 py-4 border-y border-white/6">
          {[
            { value: '50', label: 'States' },
            { value: '500+', label: 'Counties' },
            { value: 'Live', label: 'Data Feed' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-white font-bold text-lg leading-none">{s.value}</p>
              <p className="text-white/35 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <Link
          to="/pricing"
          className="flex items-center justify-center gap-2 w-full py-4 bg-amber-500 hover:bg-amber-400 text-[#070c14] font-bold rounded-xl transition-colors whitespace-nowrap mb-3"
        >
          <i className="ri-vip-crown-line" />
          View Membership Plans
        </Link>
        <Link
          to="/login"
          className="flex items-center justify-center gap-2 w-full py-3.5 border border-white/15 text-white/55 font-semibold rounded-xl hover:border-amber-500/40 hover:text-white/80 transition-colors whitespace-nowrap text-sm"
        >
          <i className="ri-login-box-line" />
          Already a Member? Sign In
        </Link>
      </div>
    </div>
  );
}

/* ── Main section ────────────────────────────────────────────── */
export default function TaxDelinquentMap() {
  const [filter, setFilter] = useState<RegionFilter>('ALL');
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<TaxProperty | null>(null);

  useEffect(() => {
    setIsMember(localStorage.getItem('fr_member') === 'true');
  }, []);

  /* Reset state chip when region changes */
  const handleRegionChange = (r: RegionFilter) => {
    setFilter(r);
    setStateFilter(null);
  };

  /* Build state chips for the active region */
  const stateChips = (() => {
    const source = filter === 'ALL'
      ? TAX_PROPERTIES
      : TAX_PROPERTIES.filter((p) => p.region === filter);
    const counts: Record<string, number> = {};
    source.forEach((p) => { counts[p.state] = (counts[p.state] || 0) + 1; });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([state, count]) => ({ state, count }));
  })();

  const regionColor = filter === 'ALL' ? '#f59e0b' : (REGION_CONFIG[filter as keyof typeof REGION_CONFIG]?.color ?? '#f59e0b');

  const FILTERS: { id: RegionFilter; label: string }[] = [
    { id: 'ALL',       label: `All States (${countByRegion('ALL')})` },
    { id: 'NORTHEAST', label: `Northeast (${countByRegion('NORTHEAST')})` },
    { id: 'SOUTHEAST', label: `Southeast (${countByRegion('SOUTHEAST')})` },
    { id: 'MIDWEST',   label: `Midwest (${countByRegion('MIDWEST')})` },
    { id: 'SOUTHWEST', label: `Southwest (${countByRegion('SOUTHWEST')})` },
    { id: 'WEST',      label: `West (${countByRegion('WEST')})` },
  ];

  const activeCount = (() => {
    let base = filter === 'ALL' ? TAX_PROPERTIES : TAX_PROPERTIES.filter((p) => p.region === filter);
    if (stateFilter) base = base.filter((p) => p.state === stateFilter);
    return base.length;
  })();

  const activeLabel = stateFilter
    ? stateFilter
    : filter === 'ALL'
    ? 'all 50 states'
    : `the ${REGION_CONFIG[filter as keyof typeof REGION_CONFIG]?.label} region`;

  return (
    <section className="bg-[#090b10] py-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-red-400 text-xs font-semibold tracking-widest uppercase">Live Data Feed</span>
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white leading-tight">
              Live Tax-Delinquent Property Map
            </h2>
            <p className="text-white/45 text-sm mt-2 max-w-xl">
              Delinquent properties tracked across all 50 US states — click any pin to view details and request acquisition services.
            </p>
          </div>

          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <div className="flex items-center gap-2 bg-white/4 border border-white/8 rounded-xl px-4 py-2.5">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-white/60 text-xs">Properties tracked across</span>
              <span className="text-white font-semibold text-xs">50 States · 5 Regions</span>
            </div>
          </div>
        </div>

        {/* Region filter pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const color  = f.id === 'ALL' ? '#f59e0b' : REGION_CONFIG[f.id as keyof typeof REGION_CONFIG]?.color ?? '#f59e0b';
            return (
              <button
                key={f.id}
                onClick={() => handleRegionChange(f.id)}
                style={active ? { borderColor: color, color, background: `${color}15` } : {}}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  active ? 'border-current' : 'border-white/10 text-white/40 hover:border-white/20 hover:text-white/60'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* State chips — second row */}
        {stateChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {/* "All" chip for current region */}
            <button
              onClick={() => setStateFilter(null)}
              style={!stateFilter ? { borderColor: regionColor, color: regionColor, background: `${regionColor}15` } : {}}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                !stateFilter ? 'border-current' : 'border-white/8 text-white/30 hover:border-white/18 hover:text-white/50'
              }`}
            >
              All
            </button>
            {stateChips.map(({ state, count }) => {
              const active = stateFilter === state;
              return (
                <button
                  key={state}
                  onClick={() => setStateFilter(active ? null : state)}
                  style={active ? { borderColor: regionColor, color: regionColor, background: `${regionColor}15` } : {}}
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    active ? 'border-current' : 'border-white/8 text-white/30 hover:border-white/18 hover:text-white/50'
                  }`}
                >
                  {state} <span className="opacity-60">({count})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Map container */}
        <div className="relative w-full rounded-2xl overflow-hidden border border-white/8" style={{ height: '640px' }}>

          {/* Blurred map layer when not a member */}
          <div className={`absolute inset-0 transition-all duration-300 ${!isMember ? 'blur-sm brightness-50 pointer-events-none' : ''}`}>
            <PropertyMap
              filter={filter}
              stateFilter={stateFilter}
              onPropertySelect={setSelectedProperty}
            />

            {/* Region color legend */}
            <div className="absolute bottom-4 left-4 z-[1000] flex flex-col gap-2 bg-[#0d1520]/90 backdrop-blur-sm border border-white/8 rounded-xl px-4 py-3">
              <p className="text-white/30 text-[10px] uppercase tracking-widest mb-0.5">Region Key</p>
              {(Object.entries(REGION_CONFIG) as [string, { color: string; label: string }][]).map(([key, cfg]) => (
                <div key={key} className="flex items-center gap-2.5">
                  <span className="w-3 h-3 rounded-full flex-shrink-0 border border-white/25" style={{ background: cfg.color }} />
                  <span className="text-white/65 text-xs whitespace-nowrap">
                    <span className="font-semibold text-white/90">{cfg.label}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* Region counts — top left inside map */}
            <div className="absolute top-4 left-4 z-[1000] flex flex-wrap gap-2">
              {(Object.entries(REGION_CONFIG) as [string, { color: string; label: string }][]).map(([key, cfg]) => (
                <div
                  key={key}
                  className="bg-[#0d1520]/90 backdrop-blur-sm border border-white/8 rounded-lg px-3 py-2 text-center"
                >
                  <p className="font-bold text-sm leading-none" style={{ color: cfg.color }}>
                    {countByRegion(key as RegionFilter)}
                  </p>
                  <p className="text-white/35 text-[10px] mt-0.5">{cfg.label.slice(0, 2).toUpperCase()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Paywall overlay */}
          {!isMember && <PaywallOverlay />}
        </div>

        {/* Bottom row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-white/5">
          <p className="text-white/35 text-sm">
            Showing{' '}
            <span className="text-white/70 font-semibold">{activeCount}</span>{' '}
            sample properties across {activeLabel}
            {!isMember && (
              <span className="text-amber-400/70 ml-2 text-xs">
                — member access unlocks 10,000+ live listings
              </span>
            )}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-[#070c14] font-bold rounded-xl transition-colors duration-200 whitespace-nowrap text-sm cursor-pointer"
          >
            <i className="ri-customer-service-2-line" />
            Request Acquisition Services
          </a>
        </div>
      </div>

      {/* Property Detail Sidebar */}
      {selectedProperty && (
        <PropertySidebar
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
        />
      )}
    </section>
  );
}
